/**
 * What a visitor is told when a public write is refused.
 *
 * `src/api/client.ts` deliberately does not repeat core's message: those are contract text,
 * and several of them describe conditions ("Origin is not allowed", "Send application/json")
 * that a visitor can do nothing about and should never have to read. The code is the stable
 * part of the contract, so the copy is keyed off the code here instead.
 *
 * Two rules govern the wording. Say what happened and what to do next — never apologise,
 * never be vague. And never invent an explanation the response does not support: an
 * unrecognised code gets the honest generic, not a guess.
 */
import type { ApiError } from "../api/client";

const SHARED: Record<string, string> = {
  NETWORK_UNAVAILABLE:
    "Dealbid couldn’t reach the deals service. Check your connection and try again.",
  /*
   * The two rate-limit codes, which differ only in how sure the browser is.
   *
   * `RATE_LIMITED` is core's contract code (canon `contracts/API.md` § Error shape),
   * answered by the Cloudflare edge; `LIKELY_RATE_LIMITED` is this client's own conclusion
   * when a write failed unreadably while core was still answering an unmetered read
   * (`src/api/client.ts`). The wording follows the difference — "turned them away"
   * versus "normally means" — because the second is an inference and must not be dressed
   * up as an observation.
   *
   * Neither states a wait. The mitigation window belongs to whichever edge rule fired and
   * the browser was never told which one, so naming a number would be invented urgency
   * (`AGENTS.md` § 11). "Retrying immediately won’t help" is the part that is known: while
   * the limit holds, another request is refused the same way.
   */
  RATE_LIMITED:
    "Too many requests have come from your connection, so Dealbid is turning them away for now. Retrying immediately won’t help — wait, then try again.",
  LIKELY_RATE_LIMITED:
    "Dealbid is reachable but turned this request away. That normally means too many requests have come from your connection. Retrying immediately won’t help — wait, then try again.",
  VALIDATION_FAILED: "Some details need attention before this can be sent.",
  PAYLOAD_TOO_LARGE: "That’s longer than Dealbid accepts. Shorten it and try again.",
};

/*
 * There is no submission table here any more. Canon decision `D-008` moved submission into
 * the signed-in seller app, which owns the copy for its own refusals (`APP-009`); a second
 * set kept in this repo would be wording for a form this site no longer shows.
 */
export const SUBSCRIPTION_MESSAGES: Record<string, string> = { ...SHARED };

export const REPORT_MESSAGES: Record<string, string> = {
  ...SHARED,
  NOT_FOUND:
    "This deal isn’t on the public board any more, so there’s nothing to report against it.",
};

/**
 * The generic. It stays honest about the two things that are actually known: the request
 * did not succeed, and trying again is reasonable.
 */
const GENERIC = "Dealbid couldn’t complete that. Try again in a moment.";

export function messageForError(
  error: ApiError | null,
  messages: Record<string, string>,
): string {
  if (error === null) return messages.VALIDATION_FAILED ?? GENERIC;
  return messages[error.code] ?? GENERIC;
}
